import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus,
  Edit,
  Trash2,
  Book,
  Calendar,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Copy,
  Save,
  FileText,
  Upload
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyDay = {
  dia: 1,
  data: "",
  leitura: "",
  tema: "",
  versiculo: "",
  contemplacao: "",
  pratica: "",
  declaracao: ""
};

export default function DevotionalConfigPage() {
  const { isAdmin, currentBranch, branches } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [editingDayIndex, setEditingDayIndex] = useState(null);

  const [form, setForm] = useState({
    nome: "",
    descricao: "",
    filial_id: "",
    ativo: true,
    dias: []
  });

  const [dayForm, setDayForm] = useState({ ...emptyDay });
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkParsing, setBulkParsing] = useState(false);
  const [parsedDays, setParsedDays] = useState([]);

  useEffect(() => {
    loadPlans();
  }, [currentBranch]);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API}/devotional-plans`, {
        params: currentBranch ? { filial_id: currentBranch.id } : {}
      });
      setPlans(response.data);
    } catch (error) {
      console.error("Error loading plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.filial_id) {
      toast.error("Selecione uma unidade");
      return;
    }
    if (!form.nome) {
      toast.error("Informe o nome do plano");
      return;
    }

    try {
      if (editingPlan) {
        await axios.put(`${API}/devotional-plans/${editingPlan.id}`, form);
        toast.success("Plano atualizado!");
      } else {
        await axios.post(`${API}/devotional-plans`, form);
        toast.success("Plano criado!");
      }
      setDialogOpen(false);
      resetForm();
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar plano");
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      nome: plan.nome,
      descricao: plan.descricao || "",
      filial_id: plan.filial_id,
      ativo: plan.ativo !== false,
      dias: plan.dias || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover este plano?")) return;
    try {
      await axios.delete(`${API}/devotional-plans/${id}`);
      toast.success("Plano removido");
      loadPlans();
    } catch (error) {
      toast.error("Erro ao remover plano");
    }
  };

  const resetForm = () => {
    setEditingPlan(null);
    setForm({
      nome: "",
      descricao: "",
      filial_id: currentBranch?.id || "",
      ativo: true,
      dias: []
    });
  };

  // Day management
  const openDayDialog = (index = null) => {
    if (index !== null) {
      setEditingDayIndex(index);
      setDayForm({ ...form.dias[index] });
    } else {
      setEditingDayIndex(null);
      setDayForm({
        ...emptyDay,
        dia: form.dias.length + 1
      });
    }
    setDayDialogOpen(true);
  };

  const saveDay = () => {
    if (!dayForm.data || !dayForm.leitura) {
      toast.error("Preencha pelo menos a data e a leitura");
      return;
    }

    const updatedDias = [...form.dias];
    if (editingDayIndex !== null) {
      updatedDias[editingDayIndex] = dayForm;
    } else {
      updatedDias.push(dayForm);
    }

    // Sort by day number
    updatedDias.sort((a, b) => a.dia - b.dia);

    setForm({ ...form, dias: updatedDias });
    setDayDialogOpen(false);
    setEditingDayIndex(null);
    setDayForm({ ...emptyDay });
  };

  const removeDay = (index) => {
    const updatedDias = form.dias.filter((_, i) => i !== index);
    // Renumber days
    updatedDias.forEach((d, i) => { d.dia = i + 1; });
    setForm({ ...form, dias: updatedDias });
  };

  const duplicateDay = (index) => {
    const day = { ...form.dias[index] };
    day.dia = form.dias.length + 1;
    day.data = "";
    setForm({ ...form, dias: [...form.dias, day] });
    toast.success("Dia duplicado! Ajuste a data.");
  };

  const toggleDayExpand = (index) => {
    setExpandedDays(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getBranchName = (filialId) => {
    const branch = branches?.find(b => b.id === filialId);
    return branch?.nome || "Unidade";
  };

  // Parse bulk text input using backend API
  const handleBulkImport = async () => {
    setBulkImportOpen(true);
    setBulkText("");
    setParsedDays([]);
  };

  const parseBulkText = async () => {
    if (!bulkText.trim()) {
      toast.error("Cole o texto dos devocionais");
      return;
    }

    setBulkParsing(true);
    try {
      const response = await axios.post(`${API}/devotional-plans/parse-bulk`, {
        text: bulkText
      });
      
      if (response.data.dias && response.data.dias.length > 0) {
        setParsedDays(response.data.dias);
        toast.success(`${response.data.total} dias encontrados!`);
      } else {
        toast.error("Não foi possível encontrar dias no texto. Verifique o formato.");
      }
    } catch (error) {
      toast.error("Erro ao processar o texto");
      console.error(error);
    } finally {
      setBulkParsing(false);
    }
  };

  const confirmBulkImport = () => {
    if (parsedDays.length === 0) {
      toast.error("Nenhum dia para importar");
      return;
    }

    // Renumber days based on existing + new
    const newDias = parsedDays.map((day, idx) => ({
      ...day,
      dia: form.dias.length + idx + 1
    }));

    setForm({ ...form, dias: [...form.dias, ...newDias] });
    toast.success(`${newDias.length} dias importados!`);
    setBulkImportOpen(false);
    setBulkText("");
    setParsedDays([]);
  };

  if (!isAdmin()) {
    return (
      <div className="p-4 text-center">
        <p className="text-muted-foreground">Acesso restrito a administradores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Planos de Devocional</h1>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-devotional-plan">
          <Plus className="h-4 w-4 mr-2" />
          Novo Plano
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Carregando...</p>
      ) : plans.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Book className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum plano de devocional criado</p>
            <p className="text-sm text-muted-foreground mt-2">
              Crie um plano para que os usuários possam acompanhar a leitura diária.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {plans.map(plan => (
            <Card key={plan.id} data-testid={`devotional-plan-${plan.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Book className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">{plan.nome}</h3>
                      <p className="text-sm text-muted-foreground">
                        {getBranchName(plan.filial_id)} • {plan.dias?.length || 0} dias
                      </p>
                    </div>
                    <Badge variant={plan.ativo ? "default" : "secondary"}>
                      {plan.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEdit(plan)}
                      data-testid={`edit-plan-${plan.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => handleDelete(plan.id)}
                      data-testid={`delete-plan-${plan.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Plan Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? "Editar Plano de Devocional" : "Novo Plano de Devocional"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>Nome do Plano *</Label>
                <Input
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Ex: Devocional de Março"
                  data-testid="plan-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Unidade *</Label>
                <Select
                  value={form.filial_id}
                  onValueChange={(v) => setForm({ ...form, filial_id: v })}
                >
                  <SelectTrigger data-testid="select-branch">
                    <SelectValue placeholder="Selecione a unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches?.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <Label>Descrição</Label>
              <Textarea
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Descrição opcional do plano"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Switch
                checked={form.ativo}
                onCheckedChange={(checked) => setForm({ ...form, ativo: checked })}
              />
              <Label>Plano ativo</Label>
            </div>

            {/* Days Section */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Dias do Plano ({form.dias.length})</h3>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={handleBulkImport}>
                    <Copy className="h-4 w-4 mr-1" />
                    Importar Texto
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => openDayDialog()}>
                    <Plus className="h-4 w-4 mr-1" />
                    Adicionar Dia
                  </Button>
                </div>
              </div>

              <ScrollArea className="flex-1 border rounded-lg p-2">
                {form.dias.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Nenhum dia adicionado. Clique em "Adicionar Dia" ou "Importar Texto".
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.dias.map((day, index) => (
                      <div
                        key={index}
                        className="border rounded-lg bg-muted/30"
                      >
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer"
                          onClick={() => toggleDayExpand(index)}
                        >
                          <div className="flex items-center gap-3">
                            <GripVertical className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <span className="font-medium">Dia {day.dia}</span>
                              <span className="text-muted-foreground mx-2">—</span>
                              <span className="text-sm">{day.data}</span>
                              <span className="text-muted-foreground mx-2">|</span>
                              <span className="text-sm text-muted-foreground">{day.leitura}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); duplicateDay(index); }}
                              title="Duplicar"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); openDayDialog(index); }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              onClick={(e) => { e.stopPropagation(); removeDay(index); }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {expandedDays[index] ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </div>
                        </div>
                        {expandedDays[index] && (
                          <div className="px-3 pb-3 space-y-2 text-sm border-t pt-3">
                            {day.tema && (
                              <div><strong>Tema:</strong> {day.tema}</div>
                            )}
                            {day.versiculo && (
                              <div><strong>Versículo:</strong> {day.versiculo}</div>
                            )}
                            {day.contemplacao && (
                              <div><strong>Contemplação:</strong> {day.contemplacao}</div>
                            )}
                            {day.pratica && (
                              <div><strong>Prática:</strong> {day.pratica}</div>
                            )}
                            {day.declaracao && (
                              <div><strong>Declare:</strong> {day.declaracao}</div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-devotional-plan">
                <Save className="h-4 w-4 mr-2" />
                {editingPlan ? "Salvar" : "Criar Plano"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Day Edit Dialog */}
      <Dialog open={dayDialogOpen} onOpenChange={setDayDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDayIndex !== null ? `Editar Dia ${dayForm.dia}` : "Novo Dia"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Dia Nº *</Label>
                <Input
                  type="number"
                  min="1"
                  value={dayForm.dia}
                  onChange={(e) => setDayForm({ ...dayForm, dia: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Data (DD/MM) *</Label>
                <Input
                  value={dayForm.data}
                  onChange={(e) => setDayForm({ ...dayForm, data: e.target.value })}
                  placeholder="29/03"
                />
              </div>
              <div className="space-y-2 col-span-1">
                <Label>Leitura *</Label>
                <Input
                  value={dayForm.leitura}
                  onChange={(e) => setDayForm({ ...dayForm, leitura: e.target.value })}
                  placeholder="Dt 13–14 | Mc 5"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tema</Label>
              <Input
                value={dayForm.tema}
                onChange={(e) => setDayForm({ ...dayForm, tema: e.target.value })}
                placeholder="Fé que cura e restaura dignidade"
              />
            </div>

            <div className="space-y-2">
              <Label>Versículo</Label>
              <Textarea
                value={dayForm.versiculo}
                onChange={(e) => setDayForm({ ...dayForm, versiculo: e.target.value })}
                placeholder='"Filha, a tua fé te salvou." (Mc 5:34)'
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Contemplação</Label>
              <Textarea
                value={dayForm.contemplacao}
                onChange={(e) => setDayForm({ ...dayForm, contemplacao: e.target.value })}
                placeholder="Deus chama o povo a fidelidade e identidade..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Prática</Label>
              <Textarea
                value={dayForm.pratica}
                onChange={(e) => setDayForm({ ...dayForm, pratica: e.target.value })}
                placeholder="Ore hoje pelo nome da sua dor..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Declaração</Label>
              <Textarea
                value={dayForm.declaracao}
                onChange={(e) => setDayForm({ ...dayForm, declaracao: e.target.value })}
                placeholder="Jesus, eu creio. Eu recebo cura..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDayDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="button" onClick={saveDay}>
                {editingDayIndex !== null ? "Salvar Dia" : "Adicionar Dia"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={bulkImportOpen} onOpenChange={setBulkImportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Importar Dias em Lote
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto space-y-4">
            <div className="space-y-2">
              <Label>Cole o texto dos devocionais</Label>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={`Cole aqui o texto no formato:

☐ 01/03 — Lv 20–21 | Mt 5

Sal e luz: presença que aparece

Versículo: "Vós sois a luz do mundo." (Mt 5:14)

Contemplação: Santidade, em Levítico...

Prática: Escolha uma atitude concreta...

Declare: Senhor, eu sou Teu...


☐ 02/03 — Lv 22–23 | Mt 6

O secreto sustenta o coração
...`}
                rows={10}
                className="font-mono text-sm"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                onClick={parseBulkText} 
                disabled={bulkParsing || !bulkText.trim()}
                variant="outline"
              >
                {bulkParsing ? "Processando..." : "Analisar Texto"}
              </Button>
            </div>

            {parsedDays.length > 0 && (
              <div className="space-y-2">
                <Label className="text-green-600">
                  ✓ {parsedDays.length} dias encontrados:
                </Label>
                <ScrollArea className="h-[200px] border rounded-md p-3">
                  <div className="space-y-2">
                    {parsedDays.map((day, idx) => (
                      <div key={idx} className="p-2 bg-muted rounded text-sm">
                        <div className="font-medium">
                          Dia {day.dia} ({day.data}) - {day.leitura}
                        </div>
                        <div className="text-muted-foreground text-xs mt-1">
                          {day.tema && <span className="block">Tema: {day.tema.substring(0, 50)}...</span>}
                          {day.versiculo && <span className="block">Versículo: {day.versiculo.substring(0, 50)}...</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setBulkImportOpen(false);
                setBulkText("");
                setParsedDays([]);
              }}
            >
              Cancelar
            </Button>
            <Button 
              type="button" 
              onClick={confirmBulkImport}
              disabled={parsedDays.length === 0}
            >
              <Upload className="h-4 w-4 mr-2" />
              Importar {parsedDays.length} Dias
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
